import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';
import { Alert, Dialog, DialogActions, DialogContent, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { MerSETASDIService } from '../../../../../../Component/Api/merSETA/Internal/MerSETASDIService';
import AutocompleteWithInfo, { IDropDownOptionsWithDescription } from '../../../../../../Component/Common/Components/Autocomplete/AutocompleteWithInfo';
import { tooltipColour } from '../../../../../../Component/Common/Components/BEE123.Constants';
import { MDButton } from '../../../../../../Component/Common/Components/MDButton';
import { SkeletonLoading } from '../../../../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { MerSetaSDILookupDto, MerSetaSDITrainerDetailDto, MerSetaSDITrainerDto, MerSetaSdi } from '../../../../../../Component/Model/Dto/WSP/MerSETASDI';
import { EmployeeTypes, Months } from '../../../../../../Component/Model/Enum/BEE123Enums';
import { isNullOrEmpty } from '../../../../../../Component/Model/helpers';
import { CurrencyFormatter } from '../../../../../../Helpers/CurrencyFormatter';
import { getDateAsString } from '../../../../../../Helpers/DateFormatter';
import { getIOptionsArrayWithInfo } from '../../../../../../Helpers/DropdownWithDescriptionHelper';
import { CustomDialogTitle } from '../../../../../Common/Custom/CustomDialogTitle';
import { useRecoilValue } from 'recoil';
import { userRolesState } from '../../../../../../State/user.Atom';
import { PlatformPeopleSalaryEditRole, PlatformPeopleSalaryViewRole, UserAdminRole } from '../../../../../../Component/Common/Constants/UserRoles';

interface IProps {
    merSetaSDI: MerSetaSdi;
    selectedPlatformPeopleId?: string;
    selectedTrainerName: string;
    handleClose: () => void;
    showModal: boolean;
}

export const InternalTrainersModal = (props: IProps) => {
    const userRoles = useRecoilValue(userRolesState);
    const [isSaving, setIsSaving] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [unassignedTrainerOptions, setUnassignedTrainerOptions] = React.useState<IDropDownOptionsWithDescription[]>([]);
    const [unassignedTrainers, setUnassignedTrainers] = React.useState<MerSetaSDILookupDto[]>([]);

    const [trainer, setTrainer] = React.useState<MerSetaSDITrainerDto | undefined>(undefined);

    const [trainerHasError, setTrainerHasError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");

    const { control, watch } = useForm({
        mode: 'all'
    });

    const trainerToCapture = watch("trainer") as IDropDownOptionsWithDescription;
    const [costErrors, setCostErrors] = React.useState<number[]>([]);
    const saveDisabled = (trainerToCapture == null && props.selectedPlatformPeopleId == null) || trainerHasError || costErrors.length > 0;

    const editRoleAccess = [
        PlatformPeopleSalaryViewRole,
        PlatformPeopleSalaryEditRole,
        UserAdminRole
    ]

    const canEditAccess = [
        PlatformPeopleSalaryEditRole,
        UserAdminRole
    ]

    const isEditable = userRoles.some(r => editRoleAccess.includes(r));
    const canEdit = userRoles.some(r => canEditAccess.includes(r));

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (trainer != null) {
                await MerSETASDIService.UpsertTrainer(trainer);
                toast.success("Trainer saved");
                props.handleClose();
            }

        } catch (error) {
            console.log(error);
        } finally {
            setIsSaving(false);
        }
    }

    const getUnassignedTrainers = async () => {
        try {
            const response = await MerSETASDIService.GetUnassignedTrainers(props.merSetaSDI.organisationId, props.merSetaSDI.id!);
            setUnassignedTrainers(response.data);
            const unassignedTrainerOptions = getIOptionsArrayWithInfo(
                response.data,
                'platformPeopleId',
                'fullName',
                'jobTitleAndEmployeeCode'
            );

            setUnassignedTrainerOptions(unassignedTrainerOptions);

        } catch (error) {
            console.log(error);
        } finally {

        }
    }

    const checkIfTrainerCanBeAssigned = async (trainerToCapture: IDropDownOptionsWithDescription) => {
        const platformPeopleId = trainerToCapture.id as string;
        const trainer = unassignedTrainers.find(x => x.platformPeopleId === platformPeopleId);
        const employmentTypeId = trainer?.employmentTypeId;

        if (employmentTypeId === EmployeeTypes.Unemployed) {
            setErrorMessage(`Unfortunately, ${trainer?.fullName} has an employment type "Unemployed" and cannot be added as a trainer.`);
            setTrainerHasError(true);
            setTrainer(undefined);
        } else if (employmentTypeId === EmployeeTypes.Employee_Previously_Unemployed) {
            setErrorMessage(`Unfortunately, ${trainer?.fullName} has an employment type "Employee Previously Unemployed" and cannot be added as a trainer.`);
            setTrainerHasError(true);
            setTrainer(undefined);
        } else {
            setErrorMessage("");
            setTrainerHasError(false);
            fetchTrainerData(platformPeopleId);
        }
    }

    const fetchTrainerData = async (platformPeopleId: string) => {
        setIsLoading(true);
        try {
            const response = await MerSETASDIService.GetTrainerDetails(props.merSetaSDI.id!, platformPeopleId);
            setTrainer(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const getCostValue = (row: MerSetaSDITrainerDetailDto) => {
        const cost = (row.salary / 173.36) * (row.hours ?? 0);
        return cost;
    }

    const getTotalCost = () => {
        let totalCost = 0;
        if (trainer && trainer.trainerDetails) {
            trainer.trainerDetails.forEach((row) => {
                totalCost += getCostValue(row);
            });
        }
        return totalCost;
    };

    const handleHoursChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, rowIndex: number) => {
        if (!isNullOrEmpty(event.target.value)) {
            const numericValue = event.target.value.replace(/[^0-9]/g, '');

            if (Number(numericValue) > 744) {
                setCostErrors([...costErrors, rowIndex]);
            } else {
                setCostErrors(costErrors.filter(x => x !== rowIndex));
            }

            if (trainer && trainer.trainerDetails) {
                const updatedData = [...trainer.trainerDetails];
                updatedData[rowIndex] = { ...updatedData[rowIndex], hours: Number(numericValue) };
                setTrainer({ ...trainer, trainerDetails: updatedData });
            }
        } else {
            setCostErrors(costErrors.filter(x => x !== rowIndex));
            if (trainer && trainer.trainerDetails) {
                const updatedData = [...trainer.trainerDetails];
                updatedData[rowIndex] = { ...updatedData[rowIndex], hours: undefined };
                setTrainer({ ...trainer, trainerDetails: updatedData });
            }
        }
    }

    useEffect(() => {
        if (props.merSetaSDI.id! > 0 && props.merSetaSDI.organisationId > 0 && props.selectedPlatformPeopleId == null) {
            getUnassignedTrainers();
        }
    }, [props.merSetaSDI.id, props.merSetaSDI.organisationId, props.selectedPlatformPeopleId])

    useEffect(() => {
        if (props.selectedPlatformPeopleId != null) {
            fetchTrainerData(props.selectedPlatformPeopleId);
        }
    }, [props.selectedPlatformPeopleId]);

    useEffect(() => {
        if (trainerToCapture != null) {
            checkIfTrainerCanBeAssigned(trainerToCapture);
        }
    }, [trainerToCapture]);

    return (
        <Dialog open={props.showModal} onClose={props.handleClose} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
            <CustomDialogTitle id="customized-dialog-title" onClose={props.handleClose}>
                {props.selectedPlatformPeopleId ? `Edit Trainer - ${props.selectedTrainerName}` : "Capture Trainer"}
            </CustomDialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        The duration of this intervention is from {getDateAsString(props.merSetaSDI.startDate)} - {getDateAsString(props.merSetaSDI.endDate)}. Capture training hours for each month in order to calculate the total cost.
                    </Grid>

                    {!props.selectedPlatformPeopleId &&
                        <Grid item xs={6}>
                            <AutocompleteWithInfo
                                control={control}
                                name="trainer"
                                label="Find Trainer *"
                                items={unassignedTrainerOptions}
                                size="small"
                            />
                        </Grid>
                    }

                    {costErrors.length > 0 &&
                        <Grid item xs={12}>
                            <Alert severity="error">The maximum number of hours in a month is 744. Enter a valid value.</Alert>
                        </Grid>
                    }
                    {
                        trainerHasError &&
                        <Grid item xs={12}>
                            <Alert severity='warning'>{errorMessage}</Alert>
                        </Grid>
                    }

                    {isLoading &&
                        <Grid item xs={12}>
                            <SkeletonLoading formLoading FieldAmount={6} width="100%" />
                        </Grid>
                    }

                    {trainer &&
                        <Grid item xs={12}>
                            <table style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: "var(--semantics-disabled, #4d4f53)", height: '40px' }}>
                                        <th><Typography variant='body2' fontWeight="700" ml={'7px'} color='#FFF'>Month</Typography></th>
                                        {isEditable &&
                                            <th><Typography variant='body2' fontWeight="700" mr={'7px'} color='#FFF' textAlign="right">Salary</Typography></th>
                                        }
                                        <th><Typography variant='body2' fontWeight="700" color='#FFF' textAlign="center">Hours</Typography></th>
                                        {isEditable &&
                                            <th>
                                                <Typography variant='body2' fontWeight="700" mr={'7px'} color='#FFF' display="flex" alignItems="center" justifyContent="right">
                                                    Cost

                                                    <Tooltip
                                                        title={`Cost is calculated as (Salary / 173.36) x Hours`}
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    bgcolor: tooltipColour,
                                                                    '& .MuiTooltip-arrow': {
                                                                        color: tooltipColour
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                        placement="right"
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            edge="end"
                                                            aria-label="info"
                                                            style={{ marginLeft: '5px' }}
                                                        >
                                                            <InfoOutlinedIcon style={{ color: '#fff' }} />
                                                        </IconButton>
                                                    </Tooltip>

                                                </Typography>
                                            </th>
                                        }
                                    </tr>
                                </thead>
                                <tbody>
                                    {trainer.trainerDetails.map((row, index) => (
                                        <tr key={index} style={{ borderBottom: '1px solid #C9C9C980', height: '40px' }}>
                                            <td style={{ width: 200 }}><Typography variant='body2' ml={'7px'}> {`${Months[row.monthId]} ${row.year}`}</Typography></td>
                                            {isEditable &&
                                                <td style={{ width: 200 }}><Typography variant='body2' mr={'7px'} textAlign="right"> {CurrencyFormatter(row.salary)}</Typography></td>
                                            }
                                            <td style={{ width: 100 }}>
                                                <TextField
                                                    label={" "}
                                                    sx={{
                                                        padding: '3px',
                                                        '& .MuiInputLabel-shrink': {
                                                            transform: 'none',
                                                        },
                                                    }}
                                                    value={row.hours}
                                                    onChange={(e) => {
                                                        handleHoursChanged(e, index);
                                                    }}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: false
                                                    }}
                                                    inputProps={{
                                                        style: {
                                                            textAlign: 'center'
                                                        }
                                                    }}
                                                    error={costErrors.includes(index)}
                                                    disabled={!canEdit}
                                                />

                                            </td>
                                            {isEditable &&
                                                <td style={{ width: 150 }}><Typography variant='body2' mr={'7px'} textAlign="right"> {CurrencyFormatter(getCostValue(row))}</Typography></td>
                                            }
                                        </tr>
                                    ))}
                                </tbody>
                                {isEditable &&
                                    <tfoot>
                                        <tr style={{ borderBottom: '1px solid #C9C9C980', height: '40px' }}>
                                            <td colSpan={3}><Typography variant='body2' ml={'7px'} fontWeight="700">Total Cost:</Typography></td>
                                            <td><Typography variant='body2' mr={'7px'} fontWeight="700" textAlign="right">{CurrencyFormatter(getTotalCost())}</Typography></td>
                                        </tr>
                                    </tfoot>
                                }
                            </table>
                        </Grid>
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                {canEdit &&
                    <LoadingButton color='primary' variant='contained' onClick={handleSave} loading={isSaving} disabled={saveDisabled}>
                        Save
                    </LoadingButton>
                }
                <MDButton color='primary' variant='outlined' onClick={props.handleClose}>
                    Cancel
                </MDButton>
            </DialogActions>
        </Dialog>
    )
}
